/* eslint-disable camelcase */
import { notification } from '@antoree/ant-ui';
import { AxiosError, AxiosResponse } from 'axios';
import { ROLE_TEACHER, ROLE_TESTER } from 'constants/role';
import { FormattedMessage } from 'react-intl';
import { useMutation, UseMutationOptions } from 'react-query';
import { useHistory, useLocation } from 'react-router';
import httpClient from 'utils/httpClient';
import { useRetrieveCurrentUser } from '../user';

export const SIGN_IN_URL = '/v2/authentication';

export type signInBody = {
  userName: string;
  password: string;
};

export type signInResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
};

export const signIn = ({ userName, password }: signInBody) => {
  const parsedBody = {
    username: userName,
    password,
    grant_type: 'password',
    client_id: 2,
    client_secret: '2scEIzVQFUFMuBBAaiZOEbOdglZ9utwPIwNQy0GI',
  };

  return httpClient.post<signInResponse>(SIGN_IN_URL, parsedBody);
};

export const useSignIn = (
  opts?: UseMutationOptions<
    AxiosResponse<signInResponse>,
    AxiosError<any>,
    signInBody
  >,
) => {
  const history = useHistory();
  const location = useLocation();

  const { mutate, isLoading, isSuccess } = useMutation<
    AxiosResponse<signInResponse>,
    AxiosError<any>,
    signInBody
  >(signIn, {
    onError: error => {
      let errorTitle: React.ReactNode;

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;

        if (status === 401 || status === 400) {
          // Invalid credentials
          errorTitle = (
            <FormattedMessage
              id="login.error.invalid_credentials"
              defaultMessage="Tên đăng nhập hoặc mật khẩu không đúng. Vui lòng kiểm tra lại thông tin."
            />
          );
        } else if (status >= 500) {
          // Server error
          errorTitle = (
            <FormattedMessage
              id="login.error.server"
              defaultMessage="Đã xảy ra lỗi máy chủ. Vui lòng thử lại sau."
            />
          );
        } else {
          errorTitle = (
            <FormattedMessage
              id="login.error.unknown"
              defaultMessage="Đã xảy ra lỗi không xác định. Vui lòng thử lại."
            />
          );
        }
      } else if (error.request) {
        // Request made but no response (network error)
        errorTitle = (
          <FormattedMessage
            id="login.error.network"
            defaultMessage="Không thể kết nối đến máy chủ. Vui lòng kiểm tra kết nối mạng của bạn."
          />
        );
      } else {
        errorTitle = (
          <FormattedMessage
            id="login.error.unknown"
            defaultMessage="Đã xảy ra lỗi không xác định. Vui lòng thử lại."
          />
        );
      }

      notification.error({
        title: errorTitle,
      });
    },
    // eslint-disable-next-line camelcase

    ...opts,
  });

  const { isFetching } = useRetrieveCurrentUser({
    enabled: isSuccess,
    onSuccess: data => {
      const { search } = location;
      if (search === '?from-members') {
        history.push('/schedule/available-time');
        return;
      }
      history.push('/claim-request');
    },
  });

  return { mutate, isLoading: isLoading || isFetching };
};
